/**
 * Termin aus einer Kundenmail vorschlagen (Benjamin, 03.10.2026): „Wir sind am
 * Dienstag, den 06.10. vor Ort. Könnten Sie es zu 11:00 Uhr einrichten? Die
 * Adresse wäre Wolfszug 18 in 01157 Dresden" — daraus soll mit einem Tipp eine
 * Besichtigung im Planer werden.
 *
 * Nur Vorschläge aus Fremdtext: alles landet in änderbaren Feldern, nichts wird
 * ungefragt gespeichert. Lieber nichts erkennen als etwas Falsches — deshalb
 * zählen Uhrzeiten nur mit „Uhr" oder Doppelpunkt (sonst wäre „06.10" eine
 * Uhrzeit) und Adressen nur mit Postleitzahl.
 */

export type TerminVorschlag = {
  /** JJJJ-MM-TT oder leer. */
  datum: string;
  /** HH:MM oder leer. */
  zeit: string;
  strasse: string;
  plz: string;
  ort: string;
};

const MONATE: Record<string, number> = {
  jan: 1, januar: 1, jänner: 1, feb: 2, februar: 2, mär: 3, märz: 3, maerz: 3, apr: 4, april: 4, mai: 5,
  jun: 6, juni: 6, jul: 7, juli: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, okt: 10, oktober: 10,
  nov: 11, november: 11, dez: 12, dezember: 12,
};
const WOCHENTAGE: Record<string, number> = {
  sonntag: 0, montag: 1, dienstag: 2, mittwoch: 3, donnerstag: 4, freitag: 5, samstag: 6, sonnabend: 6,
};

function zwei(n: number): string {
  return String(n).padStart(2, "0");
}

function isoTag(d: Date): string {
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
}

/** Ohne Jahr: das nächste Vorkommen ab dem Tag der Mail (eine Woche Spielraum nach hinten). */
function mitJahr(tag: number, monat: number, jahr: number | null, bezug: Date): string {
  if (monat < 1 || monat > 12 || tag < 1 || tag > 31) return "";
  let j = jahr ?? bezug.getFullYear();
  if (jahr !== null && j < 100) j += 2000;
  let d = new Date(j, monat - 1, tag, 12);
  if (d.getMonth() !== monat - 1) return "";
  if (jahr === null && d.getTime() < bezug.getTime() - 7 * 86_400_000) d = new Date(j + 1, monat - 1, tag, 12);
  return isoTag(d);
}

export function datumAusText(text: string, bezug: Date): string {
  const t = text.replace(/ /g, " ");
  // 06.10. / 6.10.2026 / 06.10.26
  const zahl = /(?<![\d.])(\d{1,2})\.\s?(\d{1,2})\.(?:\s?(\d{4}|\d{2})(?!\d))?/.exec(t);
  // 6. Oktober (2026)
  const wort = /(?<!\d)(\d{1,2})\.?\s+(januar|jänner|februar|märz|maerz|april|mai|juni|juli|august|september|oktober|november|dezember|jan|feb|mär|apr|jun|jul|aug|sept?|okt|nov|dez)\.?(?:\s+(\d{4}))?/i.exec(t);
  const treffer = [zahl, wort].filter(Boolean) as RegExpExecArray[];
  treffer.sort((a, b) => a.index - b.index);
  for (const m of treffer) {
    const monat = m === zahl ? Number(m[2]) : MONATE[m[2].toLowerCase()];
    const d = mitJahr(Number(m[1]), monat, m[3] ? Number(m[3]) : null, bezug);
    if (d) return d;
  }
  const rel = /\b(übermorgen|morgen)\b/i.exec(t);
  if (rel) {
    const d = new Date(bezug.getFullYear(), bezug.getMonth(), bezug.getDate() + (rel[1].toLowerCase() === "morgen" ? 1 : 2), 12);
    return isoTag(d);
  }
  // „am Dienstag" / „nächsten Dienstag" — der nächste solche Tag nach der Mail.
  const wt = /\b(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonnabend|sonntag)\b/i.exec(t);
  if (wt) {
    const ziel = WOCHENTAGE[wt[1].toLowerCase()];
    const plus = ((ziel - bezug.getDay() + 7) % 7) || 7;
    return isoTag(new Date(bezug.getFullYear(), bezug.getMonth(), bezug.getDate() + plus, 12));
  }
  return "";
}

export function zeitAusText(text: string): string {
  const t = text.replace(/ /g, " ");
  const re = /(?<![\d.:])([01]?\d|2[0-3])(?:([:.])([0-5]\d))?(\s*Uhr\b)?(?![\d.:]?\d)/gi;
  for (let m = re.exec(t); m; m = re.exec(t)) {
    const uhr = !!m[4];
    const doppelpunkt = m[2] === ":";
    if (!uhr && !doppelpunkt) continue;
    return `${zwei(Number(m[1]))}:${m[3] || "00"}`;
  }
  return "";
}

/** Wörter vor der Hausnummer, die nicht zur Straße gehören. */
const KEINE_STRASSE = new Set([
  "adresse", "anschrift", "die", "das", "der", "ist", "wäre", "waere", "lautet", "unter", "bei", "uns", "mir",
  "grundstück", "garten", "objekt", "ort", "wohne", "wohnen", "wir", "ich", "liegt", "in", "im", "nach", "zur", "zum",
]);
const VERBINDER = new Set(["am", "an", "auf", "der", "den", "dem", "die", "im", "in", "zum", "zur", "vom", "von", "alte", "alter", "neue", "neuer"]);

export function adresseAusText(text: string): Pick<TerminVorschlag, "strasse" | "plz" | "ort"> {
  const leer = { strasse: "", plz: "", ort: "" };
  for (const zeile of text.replace(/ /g, " ").split(/\n/)) {
    // Hausnummer, dann (Komma | „in" | Leerzeichen), dann PLZ und Ort
    const m = /([A-Za-zÄÖÜäöüß.\- ]{3,60}?)\s(\d{1,4}\s?[a-zA-Z]?(?:\s?[-/]\s?\d{1,4}[a-zA-Z]?)?)\s*(?:,\s*|\s+in\s+|\s+)(\d{5})\s+([A-ZÄÖÜ][A-Za-zÄÖÜäöüß.\-]+(?:\s(?:an der|am|im|bei|[A-ZÄÖÜ])[A-Za-zÄÖÜäöüß.\-]*)?)/.exec(zeile);
    if (!m) continue;
    const woerter = m[1].trim().split(/\s+/);
    // Von hinten sammeln, bis ein Wort nicht mehr zur Straße passt.
    const strasse: string[] = [];
    for (let i = woerter.length - 1; i >= 0; i--) {
      const w = woerter[i];
      const klein = w.toLowerCase();
      if (strasse.length === 0) { strasse.unshift(w); continue; }
      if (KEINE_STRASSE.has(klein) && !VERBINDER.has(klein)) break;
      if (/^[A-ZÄÖÜ]/.test(w) || VERBINDER.has(klein)) { strasse.unshift(w); continue; }
      break;
    }
    while (strasse.length > 1 && (KEINE_STRASSE.has(strasse[0].toLowerCase()) || /^(Adresse|Anschrift)$/i.test(strasse[0]))) strasse.shift();
    // Ein Verbinder allein am Anfang ohne Großwort danach gehört nicht dazu.
    while (strasse.length > 1 && VERBINDER.has(strasse[0].toLowerCase()) && !/^[A-ZÄÖÜ]/.test(strasse[1])) strasse.shift();
    const name = strasse.join(" ");
    if (!/^[A-ZÄÖÜ]/.test(name)) continue;
    return { strasse: `${name} ${m[2].replace(/\s+/g, "")}`, plz: m[3], ort: ortOhneOrtsteil(m[4].replace(/[.,]+$/, "")) };
  }
  return adresseAusZeilen(text) || leer;
}

const PLZ_ORT = /^\s*(\d{5})\s+([A-ZÄÖÜ][A-Za-zÄÖÜäöüß.\-]+(?:\s(?:an der|am|im|bei|[A-ZÄÖÜ])[A-Za-zÄÖÜäöüß.\-]*)?)[\s.,]*$/;
const STRASSE_NR = /^\s*([A-ZÄÖÜ][A-Za-zÄÖÜäöüß.\- ]{2,60}?)\s+(\d{1,4}\s?[a-zA-Z]?(?:\s?[-/]\s?\d{1,4}[a-zA-Z]?)?)[\s.,]*$/;

/**
 * Anschrift über zwei Zeilen (13.10.2026, Anfrage Fellendorf): „Kastanienweg 40" und
 * „01156 Dresden" untereinander — in beiden Reihenfolgen, die Straße direkt über
 * oder unter der PLZ-Zeile. Weiter auseinander zählt nicht.
 */
function adresseAusZeilen(text: string): Pick<TerminVorschlag, "strasse" | "plz" | "ort"> | null {
  const zeilen = text.replace(/\u00a0/g, " ").split(/\n/).map((z) => z.trim()).filter(Boolean);
  for (let i = 0; i < zeilen.length; i++) {
    const po = PLZ_ORT.exec(zeilen[i]);
    if (!po) continue;
    for (const j of [i - 1, i + 1]) {
      const st = j >= 0 && j < zeilen.length ? STRASSE_NR.exec(zeilen[j]) : null;
      if (!st || KEINE_STRASSE.has(st[1].split(/\s+/)[0].toLowerCase())) continue;
      return { strasse: `${st[1].trim()} ${st[2].replace(/\s+/g, "")}`, plz: po[1], ort: ortOhneOrtsteil(po[2].replace(/[.,]+$/, "")) };
    }
  }
  return null;
}

export type NameFund = { anrede: "" | "familie" | "herr" | "frau"; vorname: string; nachname: string };

const VORSATZ: Record<string, NameFund["anrede"]> = { fam: "familie", familie: "familie", herr: "herr", frau: "frau" };
const GRUSS = /^(mit\s+)?(freundlichen|herzlichen|besten|lieben|viele|beste|liebe|schöne)?\s*(grüßen|grüße|gruß|gruesse|gruessen)\b|^(mfg|lg|vg)\b/i;
const NAMENSWORT = /^[A-ZÄÖÜ][a-zäöüß]+(?:-[A-ZÄÖÜ][a-zäöüß]+)?$/;

/**
 * Der Name aus dem Text der Mail — wenn der Absender nur „P" heißt (13.10.2026,
 * Anfrage Fellendorf). Erst eine Zeile wie „Fam. Fellendorf" / „Herr Max Müller",
 * sonst die Zeile unter dem Gruß („Mit freundlichen Grüßen / Anna Weber").
 * Höchstens drei Wörter, alle großgeschrieben — lieber nichts als ein Satzanfang.
 */
export function nameAusText(text: string): NameFund | null {
  const zeilen = text.replace(/\u00a0/g, " ").split(/\n/).map((z) => z.trim()).filter(Boolean);
  const lesen = (zeile: string, anrede: NameFund["anrede"]): NameFund | null => {
    const w = zeile.replace(/[,.!]+$/, "").split(/\s+/);
    if (!w.length || w.length > 3 || !w.every((x) => NAMENSWORT.test(x))) return null;
    return { anrede, vorname: w.length > 1 && anrede !== "familie" ? w.slice(0, -1).join(" ") : "", nachname: w[w.length - 1] };
  };
  for (const z of zeilen) {
    const m = /^(fam\.?|familie|herr|frau)\s+(.+)$/i.exec(z);
    if (!m) continue;
    const fund = lesen(m[2], VORSATZ[m[1].toLowerCase().replace(/\.$/, "")] || "");
    if (fund) return fund;
  }
  for (let i = zeilen.length - 2; i >= 0; i--) {
    if (!GRUSS.test(zeilen[i])) continue;
    const fund = lesen(zeilen[i + 1], "");
    if (fund) return fund;
  }
  return null;
}

/** „Dresden Briesnitz" → „Dresden" (der Ortsteil steckt in der PLZ); „Bad Schandau" bleibt. */
function ortOhneOrtsteil(ort: string): string {
  const w = ort.split(" ");
  if (w.length < 2 || /^(bad|sankt|st\.|groß|gross|klein|neu|alt|königs|hohen|ober|nieder)$/i.test(w[0]) || /^[a-zäöü]/.test(w[1])) return ort;
  return w[0];
}

/** Alles zusammen aus einer Mail; `bezug` ist der Eingang der Mail (Jahr und Wochentag). */
export function terminAusMail(text: string, bezug: Date): TerminVorschlag {
  return { datum: datumAusText(text, bezug), zeit: zeitAusText(text), ...adresseAusText(text) };
}

/**
 * Zitierten Verlauf und Mail-Fußzeilen abschneiden — sonst liest man Datum und
 * Uhrzeit aus „Am Sa., 3. Okt. 2026 um 20:19 schrieb …" (so geschehen am
 * 03.10.2026: ein Termin am „10.10. 08:19", den niemand vorgeschlagen hatte).
 */
export function ohneZitat(text: string): string {
  const zeilen: string[] = [];
  for (const z of (text || "").split("\n")) {
    const t = z.trim();
    if (/^>/.test(t)) continue;
    if (/^(am|on)\b.{0,120}\b(schrieb|wrote)\b/i.test(t) || /^-{2,}\s*(original|ursprüngliche)/i.test(t)) break;
    if (/^(von|from):\s/i.test(t) && zeilen.length) break;
    if (/^gesendet (von|mit)\b/i.test(t)) continue;
    zeilen.push(z);
  }
  return zeilen.join("\n");
}

/** Rufnummern im Text — nur deutsche Schreibweisen, Ziffern 10–13. */
export function telefonAusText(text: string, fremd: string[] = []): string {
  const ziffernFremd = fremd.map((f) => f.replace(/\D/g, "")).filter((f) => f.length >= 6);
  const re = /(?<![\d+])(?:\+49|0049|0)[\d \t/()-]{7,18}\d/g;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    let z = m[0].replace(/\D/g, "");
    if (z.startsWith("0049")) z = `0${z.slice(4)}`;
    else if (m[0].trim().startsWith("+49")) z = `0${z.slice(2)}`;
    if (z.length < 10 || z.length > 13) continue;
    if (ziffernFremd.some((f) => f.includes(z) || z.includes(f))) continue;
    return m[0].trim();
  }
  return "";
}

export type VerlaufFund = TerminVorschlag & {
  telefon: string;
  /** Name aus dem Mailtext (Gruß, „Fam. …") — für Absender ohne brauchbaren Namen. */
  name: NameFund | null;
  /** Aus welcher Mail (Zeitpunkt) der Termin stammt — leer, wenn keiner gefunden. */
  terminAus: string;
};
type VerlaufMail = { richtung: "in" | "out"; text: string; zeitpunkt: string };

/**
 * Den ganzen Verlauf nach Daten für Kunde und Termin absuchen (Benjamin,
 * 03.10.2026: „beim Klick auf Besichtigung planen oder Als Kunde übernehmen
 * muss der Nachrichtenverlauf durchsucht werden"). Nur Mails des Kunden, die
 * neueste zuerst; je Feld gilt der erste Fund. Tag und Uhrzeit kommen aus
 * derselben Mail, damit kein Termin aus zwei Vorschlägen zusammengesetzt wird.
 * `fremd`: eigene Rufnummern (Signatur), die nie die des Kunden sind.
 */
export function ausVerlauf(mails: VerlaufMail[], fremd: string[] = []): VerlaufFund {
  const fund: VerlaufFund = { datum: "", zeit: "", strasse: "", plz: "", ort: "", telefon: "", terminAus: "", name: null };
  const eingehend = mails.filter((m) => m.richtung === "in").slice().reverse();
  for (const m of eingehend) {
    const text = ohneZitat(m.text);
    const bezug = new Date(m.zeitpunkt);
    if (!fund.datum) {
      const d = datumAusText(text, bezug);
      if (d) { fund.datum = d; fund.zeit = zeitAusText(text); fund.terminAus = m.zeitpunkt; }
    }
    if (!fund.strasse) Object.assign(fund, adresseAusText(text));
    if (!fund.telefon) fund.telefon = telefonAusText(text, fremd);
    if (!fund.name) fund.name = nameAusText(text);
  }
  return fund;
}

export type TerminAngebot = { datum: string; zeit: string; zeitpunkt: string; von_uns: boolean };

/**
 * Terminvorschläge aus den letzten Nachrichten (Benjamin, 04.10.2026: „die
 * Daten, die in den letzten, sagen wir 10 Nachrichten vorkommen, als Termin
 * vorschlagen — zuerst das zuletzt vorgeschlagene Datum"). Beide Richtungen
 * zählen: oft steht die Zusage in der eigenen Antwort („Ja, Dienstag 11:00
 * Uhr"). Je Mail ein Vorschlag, gleiche Termine nur einmal, neueste zuerst.
 */
export function terminVorschlaege(mails: VerlaufMail[], anzahl = 10): TerminAngebot[] {
  const liste: TerminAngebot[] = [];
  for (const m of mails.slice(-anzahl).reverse()) {
    const text = ohneZitat(m.text);
    const datum = datumAusText(text, new Date(m.zeitpunkt));
    if (!datum) continue;
    const zeit = zeitAusText(text);
    const gleich = liste.find((t) => t.datum === datum);
    if (gleich) {
      if (!gleich.zeit && zeit) gleich.zeit = zeit;
      continue;
    }
    liste.push({ datum, zeit, zeitpunkt: m.zeitpunkt, von_uns: m.richtung === "out" });
  }
  return liste;
}

/** HH:MM plus Minuten, höchstens 23:59. */
export function zeitPlus(zeit: string, minuten: number): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(zeit);
  if (!m) return "";
  const summe = Math.min(23 * 60 + 59, Number(m[1]) * 60 + Number(m[2]) + minuten);
  return `${zwei(Math.floor(summe / 60))}:${zwei(summe % 60)}`;
}

const WT_LANG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

/** „Dienstag, 06.10." — so steht es in der Bestätigung. */
export function tagLang(datum: string): string {
  const d = new Date(`${datum}T12:00:00`);
  if (Number.isNaN(d.getTime())) return datum;
  return `${WT_LANG[d.getDay()]}, ${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.`;
}

/**
 * Die Begrüßung, die wir in diesem Verlauf schon benutzt haben („Guten Tag,
 * Herr Witting,") — so klingt die Bestätigung wie die Mail davor.
 */
export function eigeneBegruessung(eigeneTexte: string[]): string {
  for (let i = eigeneTexte.length - 1; i >= 0; i--) {
    const erste = (eigeneTexte[i] || "").split("\n").map((z) => z.trim()).find(Boolean) || "";
    // Nur eine echte Anredezeile („Guten Tag, Herr Witting,"), kein ganzer Satz dahinter.
    if (/^(guten tag|hallo|liebe|lieber|sehr geehrte|moin|servus)\b/i.test(erste) && erste.length <= 60
      && !/[.?]$/.test(erste) && erste.split(/\s+/).length <= 7) {
      return /[,!]$/.test(erste) ? erste : `${erste},`;
    }
  }
  return "";
}

export function bestaetigungText(v: { begruessung: string; name: string; datum: string; von: string; adresse: string }): string {
  const gruss = v.begruessung || (v.name ? `Guten Tag ${v.name},` : "Guten Tag,");
  const wo = v.adresse ? ` (${v.adresse})` : "";
  return `${gruss}\n\nvielen Dank, das passt: Wir kommen am ${tagLang(v.datum)} um ${v.von} Uhr zur Besichtigung vorbei${wo}.\n\nViele Grüße`;
}

type AdressFelder = { strasse: string; plz: string; ort: string };
type KundeAdressen = AdressFelder & {
  rechnung_strasse?: string; rechnung_plz?: string; rechnung_ort?: string; rechnungsadresse?: string;
};

export function adresszeile(a: AdressFelder): string {
  const ortZeile = [a.plz, a.ort].map((s) => (s || "").trim()).filter(Boolean).join(" ");
  return [(a.strasse || "").trim(), ortZeile].filter(Boolean).join(", ");
}

const norm = (s: string) => s.replace(/[,.]/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/straße\b/g, "str").replace(/str\b/g, "str");

/**
 * Was am Kunden gespeichert wird, wenn die Besichtigung woanders ist als seine
 * Adresse (Benjamin, 03.10.2026: „Als Lieferadresse speichern").
 *
 * Die Lieferadresse sind im Kundenstamm die Felder strasse/plz/ort — der
 * Standard-Ort jedes Termins (termin_ort.py). Steht dort schon eine andere
 * Adresse und ist keine Rechnungsadresse hinterlegt, wandert die alte dorthin:
 * sonst ginge die nächste Rechnung an den Garten statt an die Wohnung.
 */
export function adressPlan(k: KundeAdressen | null | undefined, neu: AdressFelder): { patch: Record<string, string>; hinweis: string } {
  const ziel = { strasse: neu.strasse.trim(), plz: neu.plz.trim(), ort: neu.ort.trim() };
  if (!k || !ziel.strasse || !ziel.ort) return { patch: {}, hinweis: "" };
  const alt = adresszeile(k);
  if (norm(alt) === norm(adresszeile(ziel))) return { patch: {}, hinweis: "" };
  if (!(k.strasse || "").trim()) {
    return { patch: ziel, hinweis: "Die Adresse wird beim Kunden gespeichert." };
  }
  const rechnungDa = !!((k.rechnung_strasse || "").trim() || (k.rechnungsadresse || "").trim());
  if (rechnungDa) {
    return { patch: ziel, hinweis: `Wird die Lieferadresse des Kunden (bisher ${alt}). Die Rechnungsadresse bleibt.` };
  }
  return {
    patch: { ...ziel, rechnung_strasse: (k.strasse || "").trim(), rechnung_plz: (k.plz || "").trim(), rechnung_ort: (k.ort || "").trim() },
    hinweis: `Wird die Lieferadresse des Kunden. Die bisherige Adresse (${alt}) bleibt als Rechnungsadresse.`,
  };
}
